import type { SignalBody, SignalKind } from '@jerome2/shared';
import { setApiObserver } from '../api.ts';

// Signalen uit het gebruik van de app: driftig klikken (rage clicks), trage aanroepen en een
// trage eerste weergave, API-fouten en ongedaan maken. Ze worden gebundeld en elke 20 seconden
// (en bij het verlaten van de pagina) naar POST /api/signals gestuurd; de dagelijkse digest van
// Jerome maakt er wensen en fouten van. Niets hier mag de app zelf ooit laten struikelen.

export const FLUSH_MS = 20_000;
export const MERGE_WINDOW_MS = 60_000;
export const RAGE_WINDOW_MS = 1200;
export const RAGE_THRESHOLD = 3;
export const SLOW_API_MS = 4000;
export const SLOW_RENDER_MS = 3000;
const MAX_PENDING = 50;

export interface PendingSignal {
  kind: SignalKind;
  text: string;
  data: Record<string, unknown>;
  count: number;
  firstAt: number;
}

export interface SignalBuffer {
  add(kind: SignalKind, text: string, data?: Record<string, unknown>): void;
  /** Geeft alles wat klaarstaat terug (als API-lichamen) en maakt de buffer leeg. */
  flush(): SignalBody[];
  size(): number;
}

/** Buffer die dubbele meldingen binnen een minuut samenvoegt (teller in data.count). */
export function createSignalBuffer(opts: { now?: () => number; mergeWindowMs?: number; max?: number } = {}): SignalBuffer {
  const now = opts.now ?? Date.now;
  const window = opts.mergeWindowMs ?? MERGE_WINDOW_MS;
  const max = opts.max ?? MAX_PENDING;
  const pending: PendingSignal[] = [];
  return {
    add(kind, text, data = {}) {
      const clean = String(text ?? '').trim().slice(0, 1000);
      if (!clean) return;
      const t = now();
      const same = pending.find((p) => p.kind === kind && p.text === clean && t - p.firstAt < window);
      if (same) {
        same.count += 1;
        return;
      }
      if (pending.length >= max) return;
      pending.push({ kind, text: clean, data, count: 1, firstAt: t });
    },
    flush() {
      const out = pending.splice(0).map((p) => ({ kind: p.kind, text: p.text, data: { ...p.data, ...(p.count > 1 ? { count: p.count } : {}) } }));
      return out;
    },
    size: () => pending.length,
  };
}

/** Herkent driftig klikken: `threshold` klikken op hetzelfde element binnen `windowMs`; meldt één keer per uitbarsting. */
export function createRageDetector(opts: { now?: () => number; windowMs?: number; threshold?: number } = {}): { click(target: EventTarget | null): string | null } {
  const now = opts.now ?? Date.now;
  const window = opts.windowMs ?? RAGE_WINDOW_MS;
  const threshold = opts.threshold ?? RAGE_THRESHOLD;
  let last: { el: Element; times: number[] } | null = null;
  return {
    click(target) {
      const el = interactiveOf(target);
      if (!el) {
        last = null;
        return null;
      }
      const t = now();
      if (!last || last.el !== el) last = { el, times: [] };
      last.times = last.times.filter((x) => t - x < window);
      last.times.push(t);
      if (last.times.length < threshold) return null;
      last.times = [];
      return labelOf(el);
    },
  };
}

function interactiveOf(target: EventTarget | null): Element | null {
  const el = target as Element | null;
  if (!el || typeof (el as Element).closest !== 'function') return null;
  return el.closest('button, a, [role="button"], [role="option"], [role="tab"], [role="menuitem"], input, select, textarea, li, label') ?? el;
}

/** Korte, leesbare naam van een element voor in een signaal. */
export function labelOf(el: Element): string {
  const attr = el.getAttribute('aria-label') ?? el.getAttribute('title') ?? '';
  const text = attr || (el.textContent ?? '').replace(/\s+/g, ' ').trim();
  const tag = el.tagName.toLowerCase();
  const base = text || (el.getAttribute('placeholder') ?? '') || tag;
  return base.slice(0, 60);
}

/** Pad zonder ids: lange tokens, uuid's en getallen worden :id; de querystring valt weg. */
export function normalizePath(path: string): string {
  const clean = path.split('?')[0] ?? path;
  return clean
    .split('/')
    .map((seg) => (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(seg) || /^\d+$/.test(seg) || (seg.length >= 20 && /^[A-Za-z0-9_=%.-]+$/.test(seg)) ? ':id' : seg))
    .join('/');
}

const buffer = createSignalBuffer();
let installed = false;

/** Meld een signaal (wordt gebundeld verstuurd). Veilig om overal aan te roepen. */
export function signal(kind: SignalKind, text: string, data?: Record<string, unknown>): void {
  try {
    buffer.add(kind, text, data);
  } catch {
    // een signaal mag nooit iets breken
  }
}

export function pendingSignals(): number {
  return buffer.size();
}

function send(signals: SignalBody[], useBeacon: boolean): void {
  if (!signals.length) return;
  const body = JSON.stringify({ signals });
  try {
    if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      if (navigator.sendBeacon('/api/signals', new Blob([body], { type: 'application/json' }))) return;
    }
    void fetch('/api/signals', { method: 'POST', headers: { 'content-type': 'application/json' }, body, credentials: 'same-origin', keepalive: true }).catch(() => undefined);
  } catch {
    // niet te versturen; dan gaat het verloren
  }
}

/** Verstuurt wat klaarstaat; met `beacon` bij het verlaten van de pagina. */
export function flushSignals(beacon = false): void {
  send(buffer.flush(), beacon);
}

export function installSignals(target: Window = window): void {
  if (installed) return;
  installed = true;
  const rage = createRageDetector();
  target.addEventListener(
    'click',
    (e) => {
      const label = rage.click(e.target);
      if (label) signal('rage_click', `Driftig geklikt op "${label}"`, { label, path: normalizePath(target.location.pathname) });
    },
    true,
  );
  setApiObserver({
    onError: (method, path, status, message) => signal('api_error', `${method} ${normalizePath(path)} gaf ${status}: ${message.slice(0, 200)}`, { method, path: normalizePath(path), status }),
    onSlow: (method, path, ms) => signal('slow', `${method} ${normalizePath(path)} duurde ${(ms / 1000).toFixed(1)} s`, { method, path: normalizePath(path), ms: Math.round(ms) }),
  });
  if (typeof requestAnimationFrame === 'function' && typeof performance !== 'undefined') {
    requestAnimationFrame(() => {
      const ms = performance.now();
      if (ms > SLOW_RENDER_MS) signal('slow', `Eerste weergave duurde ${(ms / 1000).toFixed(1)} s`, { path: normalizePath(target.location.pathname), ms: Math.round(ms) });
    });
  }
  target.setInterval(() => flushSignals(false), FLUSH_MS);
  target.addEventListener('pagehide', () => flushSignals(true));
}
